import { Box, Divider, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import DownloadIcon from "../../../../assets/TailoredGeneration/icons/tg-dataset-download-icon.svg";
import ZipSvg from "../../../../assets/TailoredGeneration/icons/zip-icon.svg";
import InfoIcon from "../../../../assets/images/icons/InfoIcon.tsx";
import BestPractisesButton from "../../../../components/common/BestPractisesButton/BestPractisesButton.tsx";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../../../components/common/BriaButtonGroup/BriaButtonGroup";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip.tsx";
import BriaImage from "../../../../components/common/Galleries/BriaImage";
import LoadingPlaceholder from "../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import RouterConstants from "../../../../constants/RouterConstants";
import useSecureNavigate from "../../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../../hooks/useStores";
import { TgDataset, defaultDataset } from "../store/tg-dataset-store";
import styles from "./TgAdvancedDatasetViewer.module.scss";

type TgAdvancedDatasetViewerProps = {
	embeddedView?: boolean;
};

const TgAdvancedDatasetViewer = ({ embeddedView }: TgAdvancedDatasetViewerProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "datasets.advancedViewer" });
	const navigate = useSecureNavigate();
	const { projectId, datasetId } = useParams();
	const { tgStore } = useAppStore();
	const { tgDatasetStore } = tgStore;
	const [dataset, setDataset] = useState<TgDataset>(defaultDataset);
	const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
	const [captionsUrl, setCaptionsUrl] = useState<string | undefined>();
	const [loadingUrls, setLoadingUrls] = useState<boolean>(false);

	useEffect(() => {
		(async () => {
			try {
				if (datasetId) {
					const ds = await tgDatasetStore.getDataset(Number(datasetId), 4);
					setDataset(ds);
					loadDownloadUrls(ds.id);
				}
			} catch (err: any) {
				if (err.response?.status === 404) {
					navigate(RouterConstants.NEW_TG.fullPath);
				}
			}
		})();

		return () => {
			window.history.replaceState({}, "");
		};
	}, []);

	const loadDownloadUrls = async (_datasetId: number) => {
		setLoadingUrls(true);
		try {
			const urls = await tgDatasetStore.getAdvancedDatasetDownloadUrls(_datasetId);
			setDownloadUrl(urls.download_url);
			setCaptionsUrl(urls.captions_url);
		} finally {
			setLoadingUrls(false);
		}
	};

	const primaryButtonClick = () => {
		navigate(
			`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectId}/${RouterConstants.NEW_TG.MODELS.createPath}`,
		);
	};

	const downloadDataset = async () => {
		if (downloadUrl) {
			window.open(downloadUrl, "_blank");
		}
	};

	const downloadCaptions = () => {
		if (captionsUrl) {
			window.open(captionsUrl, "_blank");
		}
	};

	return (
		<LoadingPlaceholder className={styles.loadingPlaceholder} isLoading={tgDatasetStore.loadingDataset}>
			<Box className={`${styles.viewerContainer} ${embeddedView ? "" : styles.fullScreenView}`}>
				<Box className={styles.header}>
					<Typography className={styles.name}>{dataset.name}</Typography>
					<BriaButtonGroup>
						<BestPractisesButton />
						<BriaButton buttonType="primaryMedium" onClick={primaryButtonClick}>
							{t("primaryButton")}
						</BriaButton>
					</BriaButtonGroup>
				</Box>
				<Divider className={styles.sectionDivider} />

				{/* Dataset Summary Section */}
				<Box className={styles.datasetSummary}>
					<Typography className={styles.sectionTitle}>{t("datasetSummary.title")}</Typography>
					<Box className={styles.summaryGrid}>
						<Box className={styles.summaryCard}>
							<Typography className={styles.summaryLabel}>{t("datasetSummary.datasetName")}</Typography>
							<Typography className={styles.summaryValue}>{dataset.name}</Typography>
						</Box>
						<Box className={styles.summaryCard}>
							<Typography className={styles.summaryLabel}>
								{t("datasetSummary.prefix")}
								<BriaTooltip
									title={t("prefixTooltip")}
									className={styles.info}
									componentsProps={{ tooltip: { className: styles.tooltip } }}
									displayOn="click"
								>
									<InfoIcon className={styles.infoIcon} />
								</BriaTooltip>
							</Typography>
							<Typography className={styles.summaryValue}>{dataset.caption_prefix}</Typography>
						</Box>
					</Box>
					<Box className={styles.summaryGridBottom}>
						<Box className={styles.summaryCard}>
							<Typography className={styles.summaryLabel}>{t("datasetSummary.sourceFile")}</Typography>
							<Box className={styles.downloadSection}>
								<Box className={styles.zipTextContainer}>
									<img src={ZipSvg} alt={"zip"} />
									<Typography className={styles.summaryValue}>
										{`Zip File "${dataset.name}.zip"`}
									</Typography>
								</Box>
								<BriaButton
									buttonType="secondarySmall"
									loading={loadingUrls}
									disabled={!downloadUrl}
									onClick={downloadDataset}
								>
									<Box className={styles.downloadIcon}>
										<img src={DownloadIcon} alt={"zip"} />
									</Box>
									{t("datasetSummary.download")}
								</BriaButton>
							</Box>
						</Box>
						<Box className={styles.summaryCard}>
							<Typography className={styles.summaryLabel}>{t("datasetSummary.captionsType")}</Typography>
							<Box className={styles.downloadSection}>
								<Typography className={styles.summaryValue}>
									{dataset.captions_source === "automatic"
										? t("datasetSummary.autoGenerated")
										: t("datasetSummary.manualGenerated")}
								</Typography>
								{dataset.captions_source === "automatic" && (
									<BriaButton
										buttonType="secondarySmall"
										loading={loadingUrls}
										onClick={downloadCaptions}
										disabled={!captionsUrl}
									>
										<Box className={styles.downloadIcon}>
											<img src={DownloadIcon} alt={"zip"} />
										</Box>
										{t("datasetSummary.download")}
									</BriaButton>
								)}
							</Box>
						</Box>
					</Box>
				</Box>

				<Divider className={styles.sectionDividerLight} />

				{/* Sample From Dataset Section */}
				<Box className={styles.sampleSection}>
					<Typography className={styles.sectionTitle}>{t("sampleFromDataset.title")}</Typography>
					<Box className={styles.images}>
						{dataset.images.slice(0, 4).map((image) => (
							<Box className={styles.imageCard}>
								<Box className={styles.imageContainer}>
									<BriaImage
										className={styles.image}
										url={image.thumbnail_url}
										image={{
											url: image.thumbnail_url,
											id: image.id.toString(),
											src: image.thumbnail_url,
										}}
										hideFullScreenButton
									/>
								</Box>
								<Box className={styles.imageCaptionWrapper}>
									<Typography className={styles.imageCaption}>
										<Typography component="span" className={styles.imagePrefix}>
											<Typography component="span" className={styles.imagePrefixTag}>
												{t(`images.prefix`)}
											</Typography>
											<Typography component="span" className={styles.imagePrefixText}>
												{dataset.caption_prefix}
											</Typography>
										</Typography>
										<Typography component="span" className={styles.imageCaptionText}>
											{image.caption}
										</Typography>
									</Typography>
								</Box>
							</Box>
						))}
					</Box>
				</Box>
			</Box>
		</LoadingPlaceholder>
	);
};
export default observer(TgAdvancedDatasetViewer);
